import * as React from 'react';
import { useState } from 'react';

import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import type { SurveyFieldConfig } from 'bundles/expression-of-interest/components/ddp-multistep/SurveyQuestionField';
import SurveySectionForm from 'bundles/expression-of-interest/components/ddp-multistep/SurveySectionForm';
import SurveyTerminalScreen from 'bundles/expression-of-interest/components/ddp-multistep/SurveyTerminalScreen';
import type { SurveyTerminalContent } from 'bundles/expression-of-interest/components/ddp-multistep/SurveyTerminalScreen';
import usePrefillPhoneFromProfile from 'bundles/expression-of-interest/hooks/usePrefillPhoneFromProfile';
import useSurveySectionSubmission from 'bundles/expression-of-interest/hooks/useSurveySectionSubmission';
import type { SurveySectionAnswers } from 'bundles/expression-of-interest/queries/CreateOrUpdateSurveySection';
import type { EoiContext } from 'bundles/expression-of-interest/types';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import { appendUtmParametersToApplicationLink, getSplitName } from 'bundles/expression-of-interest/utils/util';
import countries from 'bundles/page/common/countries';

import _t from 'i18n!nls/expression-of-interest';

export type MultiStepEoiContent = {
  heading?: string;
  subheading?: string;
  step1Cta?: string;
  step2Cta?: string;
  terminalScreen?: SurveyTerminalContent;
};

type Props = {
  content?: MultiStepEoiContent;
  productId?: string;
  surveyVariant?: string;
  eoiContext?: EoiContext;
  applicationUrl?: string | null;
  // The server already has a finished response for this visitor, so the terminal screen shows right away.
  hasCompletedSurvey?: boolean;
};

const PERSONAL_INFO_SECTION_ID = 'personalInfo';
const EDUCATION_AND_TIMELINE_SECTION_ID = 'educationAndTimeline';
const COMPLETE_STEP = 'complete';

type Step = typeof PERSONAL_INFO_SECTION_ID | typeof EDUCATION_AND_TIMELINE_SECTION_ID | typeof COMPLETE_STEP;

const NO_EOI_CONTEXT: EoiContext = {};

const toShortAnswer = (text: string) => ({ 'org.coursera.survey.question.ShortAnswerResponse': { text } });

const TOTAL_STEPS = 2;

// Prefills from the client-side user; phone is fetched separately via `usePrefillPhoneFromProfile`.
const getPrefilledAnswers = (): Record<string, string> => {
  if (!isAuthenticatedUser()) {
    return {};
  }

  const currentUser = userGet();
  const { firstName, lastName } = getSplitName(currentUser.fullName);

  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(currentUser.email_address ? { emailAddress: currentUser.email_address } : {}),
  };
};

const getEducationLevelOptions = () => [
  { id: 'HIGH_SCHOOL_DIPLOMA', text: _t('High School Diploma') },
  { id: 'BACHELOR_DEGREE', text: _t("Bachelor's Degree") },
  { id: 'MASTERS_DEGREE', text: _t("Master's Degree") },
  { id: 'DOCTORATE_DEGREE', text: _t('Doctoral Degree') },
  { id: 'OTHER', text: _t('Other') },
];

const getStartDateOptions = () => [
  { id: 'within_3_months', text: _t('Within 3 months') },
  { id: '3_to_6_months', text: _t('3-6 months') },
  { id: '6_to_12_months', text: _t('6-12 months') },
  { id: 'just_researching', text: _t('Just researching') },
];

const MultiStepEoiSurvey = ({
  content,
  productId,
  surveyVariant,
  eoiContext = NO_EOI_CONTEXT,
  applicationUrl,
  hasCompletedSurvey = false,
}: Props) => {
  const { applyButtonData } = useEoiOptimizationsContext();
  const { trackClickButton, trackInteractEoi, trackTextInputFocus } = useEoiEventing(productId ?? '');

  const { submitSection, isSubmitting, hasSubmitError, resumed, completed, adoptResponse, markCompleted } =
    useSurveySectionSubmission({
      productId: productId ?? '',
      surveyVariant: surveyVariant ?? '',
      eoiContext,
    });
  const getInitialStep = (): Step => {
    if (completed) {
      return COMPLETE_STEP;
    }
    return resumed?.sectionId === EDUCATION_AND_TIMELINE_SECTION_ID
      ? EDUCATION_AND_TIMELINE_SECTION_ID
      : PERSONAL_INFO_SECTION_ID;
  };
  const [step, setStep] = useState<Step>(getInitialStep);
  // Only keyboard users need focus moved onto step 2; it would be a surprise for mouse users.
  const [shouldFocusEducationStep, setShouldFocusEducationStep] = useState(false);
  const [shouldFocusTerminalHeading, setShouldFocusTerminalHeading] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>(getPrefilledAnswers);
  const [phone, setPhone, hasPhoneHydrated] = usePrefillPhoneFromProfile();
  const canSubmit = Boolean(productId && surveyVariant);

  // Same action as the "Apply now" button on the non-multistep EOI confirmation screen.
  const startApplication = () => {
    trackClickButton('degree_apply_from_eoi');
    applyButtonData?.doAction?.();
    if (applicationUrl) {
      window.open(appendUtmParametersToApplicationLink(applicationUrl), '_blank', 'noopener noreferrer');
    }
  };

  const personalInfoFields: SurveyFieldConfig[] = [
    { id: 'firstName', kind: 'text', label: _t('First name'), placeholder: _t('First name') },
    { id: 'lastName', kind: 'text', label: _t('Last name'), placeholder: _t('Last name') },
    { id: 'emailAddress', kind: 'email', label: _t('Email address'), placeholder: _t('Email address') },
    { id: 'phoneNumber', kind: 'phone', value: phone, hydrated: hasPhoneHydrated, onChange: setPhone },
  ];

  const educationAndTimelineFields: SurveyFieldConfig[] = [
    {
      id: 'country',
      kind: 'dropdown',
      label: _t('Country of residence'),
      placeholder: _t('Country of residence'),
      searchable: true,
      options: countries.map((country) => ({ id: country.code, text: country.name })),
    },
    {
      id: 'highestLevelOfEducation',
      kind: 'dropdown',
      label: _t('Education level'),
      placeholder: _t('Education level'),
      options: getEducationLevelOptions(),
    },
    {
      id: 'intendedStartDate',
      kind: 'dropdown',
      label: _t('When do you want to start?'),
      placeholder: _t('When do you want to start?'),
      fullWidth: true,
      options: getStartDateOptions(),
    },
  ];

  const handleFieldChange = (fieldId: string, value: string) => {
    setAnswers((prevAnswers) => ({ ...prevAnswers, [fieldId]: value }));
  };

  const submitAndAdvance = async (
    sectionId: string,
    nextStep: Exclude<Step, typeof PERSONAL_INFO_SECTION_ID>,
    responses: SurveySectionAnswers
  ) => {
    if (canSubmit) {
      const { saved, existingResponseId } = await submitSection(sectionId, responses);
      if (existingResponseId) {
        // Step 1 was already saved earlier, so that response is waiting on step 2.
        adoptResponse(existingResponseId, EDUCATION_AND_TIMELINE_SECTION_ID);
      } else if (!saved) {
        return;
      }
      if (nextStep === COMPLETE_STEP) {
        markCompleted();
      }
    }
    setStep(nextStep);
  };

  const trackStepSubmit = (stepNumber: number) => {
    trackInteractEoi({
      eoiInteraction: 'submit_qualifying_questions',
      eoiSection: 'qualifying_questions',
      trackingIndex: stepNumber,
    });
  };

  const submitPersonalInfo = ({ viaKeyboard }: { viaKeyboard: boolean }) => {
    trackStepSubmit(1);
    setShouldFocusEducationStep(viaKeyboard);
    return submitAndAdvance(PERSONAL_INFO_SECTION_ID, EDUCATION_AND_TIMELINE_SECTION_ID, {
      firstName: toShortAnswer(answers.firstName ?? ''),
      lastName: toShortAnswer(answers.lastName ?? ''),
      emailAddress: toShortAnswer(answers.emailAddress ?? ''),
      phoneNumber: toShortAnswer(phone?.phoneNumber ?? ''),
    });
  };

  const submitEducationAndTimeline = async ({ viaKeyboard }: { viaKeyboard: boolean }) => {
    trackStepSubmit(2);
    setShouldFocusTerminalHeading(viaKeyboard);
    const responses: SurveySectionAnswers = {};
    educationAndTimelineFields.forEach((field) => {
      const selectedId = answers[field.id];
      const selectedOption = field.kind === 'dropdown' ? field.options.find(({ id }) => id === selectedId) : undefined;
      if (selectedOption) {
        responses[field.id] = {
          'org.coursera.survey.question.DropdownResponse': { id: selectedOption.id, text: selectedOption.text },
        };
      }
    });
    await submitAndAdvance(EDUCATION_AND_TIMELINE_SECTION_ID, COMPLETE_STEP, responses);
  };

  if (hasCompletedSurvey) {
    return (
      <SurveyTerminalScreen
        onStartApplication={applicationUrl ? startApplication : undefined}
        content={content?.terminalScreen}
      />
    );
  }

  const heading = content?.heading ?? _t("Get the program details to see if it's right for you");

  if (step === PERSONAL_INFO_SECTION_ID) {
    return (
      <SurveySectionForm
        key="personal-info"
        heading={heading}
        subheading={content?.subheading}
        submitLabel={content?.step1Cta || _t('Continue')}
        fields={personalInfoFields}
        values={answers}
        onFieldChange={handleFieldChange}
        onFieldFocus={(fieldId) => trackTextInputFocus(fieldId)}
        onSubmit={submitPersonalInfo}
        isSubmitting={isSubmitting}
        hasSubmitError={hasSubmitError}
        submissionDisclaimer={_t(
          'By sharing my phone number, I consent to receiving calls or texts from Coursera about educational opportunities.'
        )}
        stepNumber={1}
        totalSteps={TOTAL_STEPS}
      />
    );
  }

  if (step === EDUCATION_AND_TIMELINE_SECTION_ID) {
    return (
      <SurveySectionForm
        key="education-and-timeline"
        heading={heading}
        subheading={content?.subheading}
        submitLabel={content?.step2Cta || _t('Send me more info')}
        fields={educationAndTimelineFields}
        values={answers}
        onFieldChange={handleFieldChange}
        onFieldFocus={(fieldId) => trackTextInputFocus(fieldId)}
        onSubmit={submitEducationAndTimeline}
        isSubmitting={isSubmitting}
        hasSubmitError={hasSubmitError}
        stepNumber={2}
        totalSteps={TOTAL_STEPS}
        focusFirstFieldOnMount={shouldFocusEducationStep}
        fieldsOptional
      />
    );
  }

  return (
    <SurveyTerminalScreen
      focusHeadingOnMount={shouldFocusTerminalHeading}
      onStartApplication={applicationUrl ? startApplication : undefined}
      content={content?.terminalScreen}
    />
  );
};

export default MultiStepEoiSurvey;
